import React, { useState, useEffect, useRef } from 'react';
import { Building2, CheckCircle, Mail, AlertCircle } from 'lucide-react';

export default function EmailVerificationPage({ user, onResendEmail, onLogout, onContinue }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  const startCooldown = () => {
    setCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    setResending(true);
    setResendError('');
    try {
      await onResendEmail();
      setResent(true);
      startCooldown();
      setTimeout(() => setResent(false), 5000);
    } catch (err) {
      if (err.code === 'auth/too-many-requests') {
        setResendError('Too many attempts. Please wait a few minutes.');
      } else {
        setResendError('Failed to send email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleRefresh = async () => {
    await user.reload();
    if (user.emailVerified) {
      onContinue();
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Building2 size={24} className="text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-white tracking-tight">FlipperPro</h1>
              <p className="text-xs text-blue-300 font-medium tracking-wide uppercase"></p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
            <Mail size={40} className="text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Verify your email</h2>
          <p className="text-blue-200 text-sm mb-6">
            We've sent a verification link to
            <br />
            <span className="font-semibold text-white">{user.email}</span>
          </p>
          <div className="space-y-3">
            <button
              onClick={handleRefresh}
              className="w-full py-3.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 transition-all shadow-lg shadow-blue-500/30"
            >
              I've Verified My Email
            </button>
            <button
              onClick={handleResend}
              disabled={resending || resent || cooldown > 0}
              className="w-full py-3.5 rounded-xl text-sm font-semibold text-blue-300 bg-white/5 border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
            >
              {resending
                ? 'Sending...'
                : resent
                  ? 'Email Sent!'
                  : cooldown > 0
                    ? `Resend in ${cooldown}s`
                    : 'Resend Verification Email'}
            </button>
            <button
              onClick={onLogout}
              className="w-full py-3.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
          {resent && (
            <div className="mt-4 flex items-center justify-center gap-2 text-emerald-400">
              <CheckCircle size={16} />
              <span className="text-sm">Verification email sent!</span>
            </div>
          )}
          {resendError && (
            <div className="mt-4 flex items-center justify-center gap-2 text-red-400">
              <AlertCircle size={16} />
              <span className="text-sm">{resendError}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
